/**
 * Hook for creating a memoized SVGR API client instance.
 *
 * @module useSvgrClient
 */

import { useMemo } from 'react';
import { SvgrClient } from '@sudobility/svgr_client';
import { useEffectiveApi } from './useEffectiveApi';
import { authNetworkClient } from '../lib/auth-network-client';
import { API_URL } from '../config/constants';

/**
 * Creates and memoizes a `SvgrClient` instance for calling the conversion API.
 *
 * Uses a two-level fallback strategy for the network client:
 * 1. **Primary**: `useApiSafe()` -- the app-level API context from `SudobilityAppWithFirebaseAuth`.
 *    This is available when the full auth/API provider tree is mounted.
 * 2. **Fallback**: `authNetworkClient` -- a standalone Firebase Auth network client.
 *    Used when the API context is not yet available (e.g., during initial render).
 *
 * The client is re-created only when the resolved network client reference changes.
 *
 * @returns A memoized `SvgrClient` instance configured with the API URL and authenticated network client.
 */
export function useSvgrClient(): SvgrClient {
  const api = useEffectiveApi();
  const networkClient = api?.networkClient ?? authNetworkClient;

  return useMemo(() => {
    // Token source for the live job WebSocket (it cannot send an auth header).
    // `refreshToken` yields a fresh Firebase ID token, or the `nofb_` id in
    // fallback mode; without an API context there is none, so the client omits
    // the socket and callers fall back to polling.
    const getAuthToken = api ? () => api.refreshToken() : undefined;
    return new SvgrClient({ baseUrl: API_URL, networkClient, getAuthToken });
  }, [networkClient, api]);
}
